import { useEffect, useRef, useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Drawer, IconButton, ListItemIcon, ListItemText, Menu, MenuItem,
  Stack, TextField, Tooltip, Typography, alpha,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { WorkOrderComment, User, Team, WorkOrder } from '@cmms/types'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'

import { api } from '../../lib/api'
import { downloadBytes } from '../../lib/printHtml'
import { toast } from '../../store/snackbarStore'
import { useAuthStore } from '../../store/authStore'
import { ApprovalChain } from './components/ApprovalChain'
import { ChecklistExecution } from './components/ChecklistExecution'
import { GeofenceDistanceChip } from './components/GeofenceDistanceChip'
import { PartsPanel } from './components/PartsPanel'
import { QrCodeDialog, qrPayload } from '../../components/QrCodeDialog'
import { WorkflowPreview } from '../workflows/WorkflowPreview'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import type { Workflow, WorkOrderSignature } from '@cmms/api-client'
import { UserAvatar } from '../../components/UserAvatar'
import { SearchableSelect } from '../../components/SearchableSelect'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { useConfirm } from '../../components/ConfirmDialog'
import { AttachmentSection } from '../../components/AttachmentSection'
import { DigitalSigner } from '../../components/DigitalSigner'
import { usePermissions } from '../../hooks/usePermissions'
import { PRIORITY_META, getStatusMeta, type DummyWorkOrder } from './dummy'
import { useIsTaskModule } from './moduleContext'
import { summarizeSchedule, nextFireMoment, formatNextFire } from './scheduleSummary'
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined'

interface Props {
  wo: DummyWorkOrder | null
  onClose: () => void
  /// Fires when the user picks "Duplicate" from the overflow menu.
  /// Optional so callers that don't want the affordance can omit it —
  /// the menu item hides itself when this prop isn't supplied.
  onDuplicate?: (source: WorkOrder) => void
  /// Fires when the user picks "Edit" from the overflow menu. Same
  /// optional-hides-itself pattern as onDuplicate.
  onEdit?: (source: WorkOrder) => void
}

export function WorkOrderDetailDrawer({ wo, onClose, onDuplicate, onEdit }: Props) {
  const qc = useQueryClient()
  const me = useAuthStore((s) => s.user)
  const { formatDate, formatDateShort } = useOrgSettings()
  const [comment, setComment] = useState('')
  // Digital-signature gate. `signerAction` tracks which flow the modal
  // is supporting so the same component handles both close and ack.
  const [signerAction, setSignerAction] = useState<null | 'close' | 'acknowledge'>(null)
  const [signerBusy, setSignerBusy] = useState(false)
  const commentFileRef = useRef<HTMLInputElement>(null)
  const commentImageRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [recording, setRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingChunks = useRef<Blob[]>([])
  const recordingTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const [ackConfirmOpen, setAckConfirmOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [qrOpen, setQrOpen] = useState(false)
  const { can } = usePermissions()
  const initialRaw = wo?._raw ?? null
  const open = Boolean(wo && initialRaw)
  // Synthetic look-ahead rows aren't real WOs yet — skip every detail
  // fetch (they would 404) and render a read-only preview instead.
  const initialIsPreview = !!(initialRaw as any)?.is_preview
  // Route every WO-shaped call through the namespace that matches the
  // row's kind. Tasks share the same response shape but live under
  // `/api/v1/tasks/*` so they're gated by the `tasks:*` permission
  // family — using apiNs.* on a task would require the user
  // to also hold `work_orders:read` even though they only ever opened
  // the drawer for a task.
  const apiNs = ((initialRaw as any)?.kind === 'task') ? api.tasks : api.workOrders
  // React-query list cache key — invalidations after mutations must
  // target the *right* list query so the page the user is on actually
  // refreshes (Tasks page uses ['tasks'], WO page uses ['work-orders']).
  const listKey = ((initialRaw as any)?.kind === 'task') ? 'tasks' : 'work-orders'
  // User-facing copy. The drawer renders identical UI for both kinds
  // but the noun shown in toasts, confirmations and the QR sheet needs
  // to match what module the user opened.
  const labelLower = ((initialRaw as any)?.kind === 'task') ? 'task'      : 'work order'
  const labelCap   = ((initialRaw as any)?.kind === 'task') ? 'Task'      : 'Work order'

  // Live-fetch the work order so transitions refresh the drawer
  const woQ = useQuery({
    queryKey: ['work-order', initialRaw?.id],
    queryFn: () => apiNs.get(initialRaw!.id),
    enabled: !!initialRaw && !initialIsPreview,
  })
  const raw = woQ.data ?? initialRaw

  const navigate = useNavigate()

  const partsQ = useQuery({ queryKey: ['wo-parts', raw?.id], queryFn: () => apiNs.listParts(raw!.id), enabled: !!raw && !initialIsPreview })
  const woParts = Array.isArray(partsQ.data) ? partsQ.data : []

  const commentsQ = useQuery({ queryKey: ['wo-comments', raw?.id], queryFn: () => apiNs.listComments(raw!.id), enabled: !!raw && !initialIsPreview })
  const comments: WorkOrderComment[] = Array.isArray(commentsQ.data) ? commentsQ.data : []

  // Captured signatures — surfaced once the matching transition has
  // happened so the drawer shows who signed off and when. The list
  // includes unused (uploaded-but-not-consumed) rows too; we filter
  // on `used_at` in the render path.
  const signaturesQ = useQuery({
    queryKey: ['wo-signatures', raw?.id],
    queryFn: () => apiNs.listSignatures(raw!.id),
    enabled: !!raw && !initialIsPreview,
  })
  const woSignatures = Array.isArray(signaturesQ.data) ? signaturesQ.data : []

  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => api.users.list(), staleTime: 60_000 })
  const users: User[] = Array.isArray(usersQ.data) ? usersQ.data : []

  const teamsQ = useQuery({ queryKey: ['teams'], queryFn: () => api.teams.list(), staleTime: 60_000 })
  const teams: Team[] = Array.isArray(teamsQ.data) ? teamsQ.data : []

  // Multi-user aware: takes an array of user ids so callers can
  // add/remove individuals atomically. Legacy single-user callers
  // still work — they can pass `userIds: [uid]`. Team assignment
  // is orthogonal (team members fan out via the notification
  // dispatcher).
  const assignM = useMutation({ mutationFn: ({ userIds, teamId }: { userIds?: string[]; teamId?: string }) =>
    apiNs.assign(raw!.id, { assignee_ids: userIds ?? undefined, assigned_to_team_id: teamId || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [listKey] }); qc.invalidateQueries({ queryKey: ['work-order', raw!.id] }); toast.success('Assigned') }, onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed') })
  const executeM = useMutation({ mutationFn: () => apiNs.execute(raw!.id), onSuccess: () => { qc.invalidateQueries({ queryKey: [listKey] }); qc.invalidateQueries({ queryKey: ['work-order', raw!.id] }); toast.success('Work started') }, onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed') })
  const closeM = useMutation({
    mutationFn: (opts?: { signature_id?: string }) =>
      apiNs.close(raw!.id, opts?.signature_id ? { signature_id: opts.signature_id } : undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [listKey] })
      qc.invalidateQueries({ queryKey: ['work-order', raw!.id] })
      qc.invalidateQueries({ queryKey: ['wo-signatures', raw!.id] })
      toast.success(`${labelCap} closed`)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const acknowledgeM = useMutation({
    mutationFn: (opts?: { signature_id?: string }) =>
      apiNs.acknowledge(raw!.id, opts?.signature_id ? { signature_id: opts.signature_id } : undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [listKey] })
      qc.invalidateQueries({ queryKey: ['work-order', raw!.id] })
      qc.invalidateQueries({ queryKey: ['wo-signatures', raw!.id] })
      toast.success('Resolved')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  // If THIS WO requires a sig for the action, open the DigitalSigner
  // dialog; the actual mutation fires once the user confirms. When
  // the flag is off, fall through to the mutation directly. The flag
  // lives on the WO row, set by the creator at create time.
  const beginClose = () => {
    if (raw?.require_close_signature) setSignerAction('close')
    else closeM.mutate(undefined)
  }
  const beginAcknowledge = () => {
    setAckConfirmOpen(false)
    if (raw?.require_acknowledge_signature) setSignerAction('acknowledge')
    else acknowledgeM.mutate(undefined)
  }
  const handleSignatureConfirm = async (blob: Blob) => {
    if (!signerAction || !raw) return
    setSignerBusy(true)
    try {
      const file = new File([blob], 'signature.png', { type: 'image/png' })
      const sig = await apiNs.uploadSignature(raw.id, file, signerAction)
      if (signerAction === 'close') {
        await closeM.mutateAsync({ signature_id: sig.id })
      } else {
        await acknowledgeM.mutateAsync({ signature_id: sig.id })
      }
      setSignerAction(null)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to submit signature')
    } finally {
      setSignerBusy(false)
    }
  }
  const reopenM = useMutation({ mutationFn: () => apiNs.reopen(raw!.id), onSuccess: () => { qc.invalidateQueries({ queryKey: [listKey] }); qc.invalidateQueries({ queryKey: ['work-order', raw!.id] }); qc.invalidateQueries({ queryKey: ['wo-checklist-responses', raw!.id] }); toast.success('Reopened') }, onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed') })
  const deleteM = useMutation({ mutationFn: () => apiNs.remove(raw!.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [listKey] }); toast.success(`${labelCap} deleted`); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed') })
  const addCommentM = useMutation({ mutationFn: () => apiNs.addComment(raw!.id, comment.trim()),
    onSuccess: () => { setComment(''); qc.invalidateQueries({ queryKey: ['wo-comments', raw!.id] }) }, onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed') })
  const deleteCommentM = useMutation({
    mutationFn: (commentId: string) => apiNs.deleteComment(raw!.id, commentId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wo-comments', raw!.id] }); toast.success('Comment deleted') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const uploadCommentM = useMutation({
    mutationFn: () => apiNs.uploadCommentAttachment(raw!.id, pendingFile!,
      { caption: comment.trim() || undefined }),
    onSuccess: () => {
      setComment(''); setPendingFile(null)
      if (commentFileRef.current) commentFileRef.current.value = ''
      if (commentImageRef.current) commentImageRef.current.value = ''
      qc.invalidateQueries({ queryKey: ['wo-comments', raw!.id] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Upload failed'),
  })

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      const mr = new MediaRecorder(stream, { mimeType })
      recordingChunks.current = []
      mr.ondataavailable = (e) => { if (e.data.size > 0) recordingChunks.current.push(e.data) }
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        if (recordingTimer.current) { clearInterval(recordingTimer.current); recordingTimer.current = null }
        const blob = new Blob(recordingChunks.current, { type: mr.mimeType })
        const ext = mr.mimeType.includes('webm') ? 'webm' : 'm4a'
        setPendingFile(new File([blob], `voice-message.${ext}`, { type: mr.mimeType }))
        setRecording(false); setRecordingTime(0)
      }
      mr.start()
      mediaRecorderRef.current = mr
      setRecording(true); setRecordingTime(0)
      recordingTimer.current = setInterval(() => setRecordingTime((t) => t + 1), 1000)
    } catch {
      toast.error('Could not access microphone. Check browser permissions.')
    }
  }
  function stopRecording() { mediaRecorderRef.current?.stop(); mediaRecorderRef.current = null }
  function cancelRecording() {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null
      mediaRecorderRef.current.onstop = null
      mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop())
      mediaRecorderRef.current.stop()
      mediaRecorderRef.current = null
    }
    if (recordingTimer.current) { clearInterval(recordingTimer.current); recordingTimer.current = null }
    recordingChunks.current = []
    setRecording(false); setRecordingTime(0)
  }
  function sendComment() {
    if (pendingFile) uploadCommentM.mutate()
    else if (comment.trim()) addCommentM.mutate()
  }

  if (!raw) return <Drawer anchor="right" open={false} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 720 } } }} />

  const w = raw
  // Fallback for rows whose `work_order_assignees` join-table entry
  // never got written (e.g. a workflow-created WO before that path
  // was fixed to keep the join table in sync) — without this, the
  // picker below renders "Unassigned" even though the legacy
  // assigned_to_user_* columns (which the list card already falls
  // back to) are populated.
  const effectiveAssignees = w.assignees?.length
    ? w.assignees
    : (w.assigned_to_user_id
        ? [{ id: w.assigned_to_user_id, name: w.assigned_to_user_name ?? 'Unknown', avatar_url: w.assigned_to_user_avatar }]
        : [])
  const isPreview = !!(w as any).is_preview
  // Same drawer backs Tasks and Work Orders — module context tells us
  // which one is on-screen so the "Resolved" label swaps to "Completed"
  // in the Tasks flow.
  const isTask = useIsTaskModule()
  const st = getStatusMeta(w.status, isTask)
  const pr = PRIORITY_META[w.priority] ?? { label: w.priority, color: '#64748B', bg: '#F8FAFC' }
  const location = [w.site_name, w.location_name].filter(Boolean).join(' · ')
  // Prefer the backend-assigned, kind-aware code (TSK-00042 / WO-00042 —
  // see work-orders.notifications.js's codeFor) over recomputing one
  // locally; the id-slice fallback only fires for legacy rows from
  // before wo_code existed, but still needs the same TSK/WO branching.
  const woCode = isPreview ? 'UPCOMING' : (w.wo_code ?? `${isTask ? 'TSK' : 'WO'}-${w.id.slice(0, 6).toUpperCase()}`)
  const previewFireLabel = isPreview && w.scheduled_for
    ? (() => {
        try { return formatDate(new Date(w.scheduled_for!), true) }
        catch { return null }
      })()
    : null

  return (
    <>
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 820, md: 960 }, display: 'flex', flexDirection: 'column' } }}>

      {/* ── Top bar ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 3, py: 1.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography sx={{ fontSize: 13, fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: 'text.secondary' }}>{woCode}</Typography>
          <Chip label={st.label} size="small"
            icon={<Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: st.color, ml: '8px !important' }} />}
            sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: `${st.color}20`, color: st.color, border: '1px solid', borderColor: `${st.color}35`, '& .MuiChip-icon': { mr: -0.25 }, '& .MuiChip-label': { pl: 1 } }} />
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Copy link" arrow>
            <IconButton size="small" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/work-orders`); toast.success('Copied!') }}
              sx={{ color: 'text.secondary' }}><LinkOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
          </Tooltip>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}><MoreHorizIcon sx={{ fontSize: 18 }} /></IconButton>
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Stack>
      </Stack>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'thin' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ minHeight: '100%' }}>

          {/* ── LEFT — main content ── */}
          <Box sx={{ flex: 1, minWidth: 0, p: 4 }}>
            {/* Title */}
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.3, mb: 1.5 }}>
              {w.title}
            </Typography>

            {/* Geofence distance — informational; only renders when the
                org has the gate on and the browser has a usable position */}
            <Box sx={{ mb: 3 }}>
              <GeofenceDistanceChip siteId={w.site_id} siteName={w.site_name} />
            </Box>

            {/* Description */}
            {w.description && (
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.25 }}>
                  Description
                </Typography>
                <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'light' ? '#F8FAFC' : 'rgba(255,255,255,0.03)',
                  border: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontSize: 14.5, lineHeight: 1.7, color: 'text.primary' }}>{w.description}</Typography>
                </Box>
              </Box>
            )}

            {/* Resolution notes — set when the WO is closed with a reason
                (required on mobile-lite's close flow). The API has always
                returned this field, but nothing on web ever rendered it. */}
            {w.resolution_notes && (
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.25 }}>
                  Resolution notes
                </Typography>
                <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'light' ? '#F0FDF4' : 'rgba(16,185,129,0.06)',
                  border: '1px solid', borderColor: (t) => t.palette.mode === 'light' ? '#BBF7D0' : 'rgba(16,185,129,0.25)' }}>
                  <Typography sx={{ fontSize: 14.5, lineHeight: 1.7, color: 'text.primary' }}>{w.resolution_notes}</Typography>
                </Box>
              </Box>
            )}

            {/* Preview-mode banner — visible only on synthetic preview rows.
                The row isn't a real WO yet (the scheduler hasn't fired) so
                actions are disabled until spawn time. */}
            {isPreview && (
              <Box sx={{
                mb: 4, p: 2, borderRadius: 2,
                bgcolor: '#FFFBEB', border: '1px solid #FCD34D',
              }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#92400E', mb: 0.5 }}>
                  Upcoming — preview only
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: '#92400E', lineHeight: 1.55 }}>
                  This work order is shown ahead of time so you can plan.
                  {previewFireLabel ? ` It will be created on ${previewFireLabel}.` : ''}
                  {' '}You'll be able to start work, run the checklist, and add
                  comments once it actually spawns.
                </Typography>
              </Box>
            )}

            {/* Status actions */}
            {!isPreview && <Stack direction="row" spacing={1.25} sx={{ mb: 4 }} flexWrap="wrap" useFlexGap>
              {w.status === 'open' && (
                <Button size="small" variant="contained" onClick={() => executeM.mutate()} disabled={executeM.isPending}
                  sx={{ fontSize: 12.5, fontWeight: 600, px: 2.5, borderRadius: 2 }}>Start work</Button>
              )}
              {w.status === 'in_progress' && (
                <Button size="small" variant="contained" color="success" onClick={beginClose} disabled={closeM.isPending}
                  sx={{ fontSize: 12.5, fontWeight: 600, px: 2.5, borderRadius: 2 }}>Close (work done)</Button>
              )}
              {w.status === 'in_progress' && (
                <Button size="small" variant="outlined" onClick={() => reopenM.mutate()} disabled={reopenM.isPending}
                  sx={{ fontSize: 12.5, fontWeight: 600, px: 2.5, borderRadius: 2 }}>Back to open</Button>
              )}
              {w.status === 'closed' && w.reporter_id === me?.id && (
                <Button size="small" variant="contained" onClick={() => setAckConfirmOpen(true)} disabled={acknowledgeM.isPending}
                  sx={{ fontSize: 12.5, fontWeight: 600, px: 2.5, borderRadius: 2 }}>Acknowledge & Resolve</Button>
              )}
              {w.status === 'closed' && w.reporter_id !== me?.id && (
                <Tooltip title={`Only the reporter can acknowledge and resolve this ${labelLower}`} arrow>
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Button size="small" variant="contained" disabled
                      sx={{ fontSize: 12.5, fontWeight: 600, px: 2.5, borderRadius: 2 }}>Acknowledge & Resolve</Button>
                  </Box>
                </Tooltip>
              )}
              {w.status === 'closed' && (
                <Button size="small" variant="outlined" onClick={() => reopenM.mutate()} disabled={reopenM.isPending}
                  sx={{ fontSize: 12.5, fontWeight: 600, px: 2.5, borderRadius: 2 }}>Reopen (needs more work)</Button>
              )}
              {w.status === 'resolved' && (
                <Button size="small" variant="contained" startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={async () => {
                    try {
                      const bytes = await apiNs.downloadReportPdf(w.id)
                      const safeCode = (w.wo_code ?? w.id).replace(/[^A-Za-z0-9_-]/g, '-')
                      downloadBytes(bytes, `${isTask ? 'task' : 'work-order'}-${safeCode}.pdf`)
                    } catch { toast.error('Failed to load report') }
                  }}
                  sx={{ fontSize: 12.5, fontWeight: 600, px: 2.5, borderRadius: 2, bgcolor: '#1e293b', '&:hover': { bgcolor: '#0f172a' } }}>
                  Download report
                </Button>
              )}
            </Stack>}

            {/* Checklist execution */}
            {!isPreview && <ChecklistExecution workOrder={w} />}

            {/* Parts */}
            {!isPreview && <PartsPanel workOrder={w} parts={woParts}
              editable={w.status === 'open' || w.status === 'in_progress'} />}

            {/* Attachments */}
            {!isPreview && <AttachmentSection
              queryKey={['wo-attachments', w.id]}
              listFn={() => apiNs.listAttachments(w.id) as any}
              uploadFn={(file, onProgress) => apiNs.uploadAttachment(w.id, file, onProgress)}
              viewFn={(doc) => apiNs.viewAttachment(w.id, doc.id) as any}
              deleteFn={(docId) => apiNs.deleteAttachment(w.id, docId)}
              canEdit={w.status === 'open' || w.status === 'in_progress'}
              title="Attachments"
            />}

            {/* Digital signatures */}
            {!isPreview && <SignaturesSection signatures={woSignatures} />}

            {/* Workflow */}
            {!isPreview && <WorkflowSection workOrder={w} />}

            {/* Approval chain — visible whenever a workflow has attached
                approval steps, or when an editor wants to add ad-hoc
                approvers. Hidden entirely when there are no steps and
                the user can't add any. */}
            {!isPreview && <ApprovalChain workOrder={w} editable={can('work_orders:update')} />}

            {/* SLA */}
            {!isPreview && w.sla_policy_id && <SlaIndicator workOrder={w} />}

            {/* Recurrence */}
            {!isPreview && w.recurrence && w.recurrence !== 'none' && <RecurrenceSection workOrder={w} />}

            {/* Audit trail */}
            {!isPreview && <WoAuditTrail workOrderId={w.id} />}

            {/* Activity */}
            {!isPreview && <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 2.5 }}>
              Activity · {comments.length}
            </Typography>}

            {!isPreview && <Stack spacing={3} sx={{ mb: 4 }}>
              {comments.map((c) => {
                const isDeleted = c.deleted_at != null
                // Even after the policy change, the author or an admin can
                // soft-delete. We hide the trigger on already-deleted rows.
                const canDelete = !isDeleted && (c.author_id === me?.id || me?.role === 'org_admin')
                // Author-removal uses a neutral phrasing; admin moderation
                // surfaces the moderator name so it's transparent who
                // pulled the comment.
                const removedLabel = isDeleted
                  ? (c.deleted_by && c.deleted_by !== c.author_id
                      ? `Comment removed by ${c.deleted_by_name ?? 'an admin'}`
                      : 'Comment removed by author')
                  : null

                return (
                  <Stack key={c.id} direction="row" spacing={1.5} alignItems="flex-start"
                    sx={{ '&:hover .comment-delete': { opacity: 1 } }}>
                    <UserAvatar
                      name={c.author_name} avatarUrl={c.author_avatar} size={32}
                      sx={{
                        bgcolor: userColor(c.author_id ?? ''),
                        opacity: isDeleted ? 0.4 : 1,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.35 }}>
                        <Typography sx={{
                          fontSize: 13.5, fontWeight: 700,
                          color: isDeleted ? 'text.disabled' : undefined,
                        }}>
                          {c.author_name ?? 'Unknown'}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                          {relativeTime(new Date(c.created_at), formatDateShort)}
                        </Typography>
                      </Stack>
                      {isDeleted ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <DoNotDisturbAltOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography sx={{
                            fontSize: 13, color: 'text.disabled',
                            fontStyle: 'italic',
                          }}>
                            {removedLabel}
                          </Typography>
                        </Stack>
                      ) : (
                        <>
                          {c.file_url && (
                            <Box sx={{ mb: c.text ? 0.75 : 0 }}>
                              <CommentAttachment comment={c} />
                            </Box>
                          )}
                          {c.text && (
                            <Typography sx={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                              {c.text}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                    {canDelete && (
                      <Tooltip title="Delete comment" arrow>
                        <IconButton size="small" className="comment-delete"
                          onClick={() => {
                            if (confirm('Delete this comment? Any attachment will also be removed.'))
                            deleteCommentM.mutate(c.id)
                          }}
                          disabled={deleteCommentM.isPending}
                          sx={{ opacity: 0, transition: 'opacity 120ms', color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )
              })}
              {comments.length === 0 && (
                <Typography sx={{ fontSize: 13.5, color: 'text.disabled', fontStyle: 'italic' }}>No activity yet</Typography>
              )}
            </Stack>}

            {/* Pending attachment preview */}
            {!isPreview && pendingFile && (
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{
                px: 1.5, py: 1, mb: 1, borderRadius: 2,
                bgcolor: (t) => t.palette.mode === 'light' ? '#F5F5F7' : 'rgba(255,255,255,0.04)',
                border: '1px solid', borderColor: 'divider',
              }}>
                {pendingFile.type.startsWith('image/') ? (
                  <Box component="img" src={URL.createObjectURL(pendingFile)}
                    sx={{ width: 42, height: 42, borderRadius: 1.25, objectFit: 'cover' }} />
                ) : (
                  <Box sx={{ width: 42, height: 42, borderRadius: 1.25, bgcolor: 'primary.main',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {pendingFile.type.startsWith('audio/')
                      ? <MicNoneOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />
                      : <AttachFileOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />}
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap>{pendingFile.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{formatBytes(pendingFile.size)}</Typography>
                </Box>
                <IconButton size="small" onClick={() => {
                  setPendingFile(null)
                  if (commentFileRef.current) commentFileRef.current.value = ''
                  if (commentImageRef.current) commentImageRef.current.value = ''
                }} sx={{ color: 'text.secondary' }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>
            )}

            {/* Comment composer */}
            {!isPreview && <Stack direction="row" spacing={1.25} alignItems="center">
              <UserAvatar name={me?.name} avatarUrl={me?.avatar_url} size={30} sx={{ bgcolor: 'primary.main', flexShrink: 0 }} />
              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flex: 1, border: '1px solid',
                borderColor: (comment.trim() || pendingFile) ? 'primary.main' : 'divider', borderRadius: 99, px: 0.5,
                transition: 'border-color 150ms', '&:focus-within': { borderColor: 'primary.main' },
                bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                <Tooltip title="Attach image" arrow>
                  <IconButton size="small" onClick={() => commentImageRef.current?.click()}
                    disabled={recording}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' }, flexShrink: 0 }}>
                    <ImageOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Attach file" arrow>
                  <IconButton size="small" onClick={() => commentFileRef.current?.click()}
                    disabled={recording}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' }, flexShrink: 0 }}>
                    <AttachFileOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                <TextField value={comment} onChange={(e) => setComment(e.target.value)} fullWidth
                  placeholder={recording ? 'Recording…' : pendingFile ? 'Add a caption…' : 'Write a comment…'}
                  variant="standard" InputProps={{ disableUnderline: true }} disabled={recording}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && (comment.trim() || pendingFile)) {
                      e.preventDefault(); sendComment()
                    }
                  }}
                  onPaste={(e) => {
                    if (pendingFile) return
                    const item = Array.from(e.clipboardData.items).find((i) => i.kind === 'file')
                    const f = item?.getAsFile()
                    if (f) { e.preventDefault(); setPendingFile(f) }
                  }}
                  sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 1 } }} />
                {recording ? (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pr: 0.25 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: '#DC2626',
                      animation: 'pulse 1s infinite',
                      '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
                    <Typography sx={{ fontSize: 11.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', minWidth: 28, color: 'text.secondary' }}>
                      {Math.floor(recordingTime / 60)}:{String(recordingTime % 60).padStart(2, '0')}
                    </Typography>
                    <Tooltip title="Cancel" arrow>
                      <IconButton size="small" onClick={cancelRecording} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Stop & attach" arrow>
                      <IconButton size="small" onClick={stopRecording}
                        sx={{ bgcolor: '#DC2626', color: '#fff', width: 28, height: 28,
                          '&:hover': { bgcolor: '#B91C1C' }, flexShrink: 0 }}>
                        <StopCircleOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (comment.trim() || pendingFile) ? (
                  <IconButton size="small" onClick={sendComment}
                    disabled={addCommentM.isPending || uploadCommentM.isPending}
                    sx={{ bgcolor: 'primary.main', color: '#fff',
                      width: 28, height: 28, flexShrink: 0,
                      '&:hover': { bgcolor: 'primary.dark' }, transition: 'all 150ms' }}>
                    {(addCommentM.isPending || uploadCommentM.isPending)
                      ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                      : <SendOutlinedIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                ) : (
                  <Tooltip title="Voice message" arrow>
                    <IconButton size="small" onClick={startRecording}
                      sx={{ color: 'text.disabled', '&:hover': { color: '#DC2626' }, width: 28, height: 28, flexShrink: 0 }}>
                      <MicNoneOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>}
            {!isPreview && <input ref={commentImageRef} type="file" hidden accept="image/*"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setPendingFile(f) }} />}
            {!isPreview && <input ref={commentFileRef} type="file" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setPendingFile(f) }} />}
          </Box>

          {/* ── RIGHT — details sidebar ── */}
          <Box sx={{
            width: { xs: '100%', sm: 280 }, flexShrink: 0,
            borderLeft: { sm: '1px solid' }, borderColor: 'divider',
            p: 3.5,
          }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 2.5 }}>
              Details
            </Typography>

            <Stack spacing={3}>
              <SideField label="Priority">
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: pr.color }}>{pr.label}</Typography>
              </SideField>

              <SideField label={effectiveAssignees.length > 1 ? 'Assigned people' : 'Assigned person'}>
                <SearchableSelect
                  multiple
                  value={effectiveAssignees.map((a) => a.id)}
                  onChange={(ids) => assignM.mutate({ userIds: ids, teamId: w.assigned_to_team_id ?? undefined })}
                  options={users}
                  getValue={(u) => u.id}
                  getLabel={(u) => u.name}
                  placeholder="Unassigned"
                  disabled={isPreview || w.status === 'resolved'}
                  renderOption={(u) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: userColor(u.id) }} />
                      <Box>
                        <Typography sx={{ fontSize: 12.5 }}>{u.name}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{(u as any).role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                      </Box>
                    </Stack>
                  )}
                />
              </SideField>

              <SideField label="Assigned team">
                <SearchableSelect
                  value={w.assigned_to_team_id ?? ''}
                  onChange={(tid) => assignM.mutate({ userIds: effectiveAssignees.map((a) => a.id), teamId: tid || undefined })}
                  options={teams}
                  getValue={(t) => t.id}
                  getLabel={(t) => t.name}
                  placeholder="No team"
                  disabled={isPreview || w.status === 'resolved'}
                  renderOption={(t) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar variant="rounded" sx={{ width: 22, height: 22, bgcolor: '#7C3AED', fontSize: 10 }}><GroupsOutlinedIcon sx={{ fontSize: 14 }} /></Avatar>
                      <Box>
                        <Typography sx={{ fontSize: 12.5 }}>{t.name}</Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{t.member_count} members</Typography>
                      </Box>
                    </Stack>
                  )}
                />
              </SideField>

              <SideField label="Due">
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <ScheduleOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {w.due_date ? formatDate(w.due_date) : '—'}
                  </Typography>
                </Stack>
              </SideField>

              {w.work_type && (
                <SideField label="Type">
                  <Typography sx={{ fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>{w.work_type}</Typography>
                </SideField>
              )}

              {w.estimated_minutes != null && w.estimated_minutes > 0 && (
                <SideField label="Estimated">
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <ScheduleOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      {formatMinutes(w.estimated_minutes)}
                    </Typography>
                  </Stack>
                </SideField>
              )}

              {/* Actual work time — In Progress → Closed. Always pinned
                  to closed_at (never resolved_at) so it doesn't silently
                  mix "time spent fixing it" with "time spent waiting on
                  the reporter to acknowledge it" — that gap has its own
                  "Closed → Resolved" field below. Same definition as the
                  emailed completion report's "Total Duration". */}
              {w.closed_at && (
                <SideField label="Duration">
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <ScheduleOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      {formatMinutes(durationMinutes(w.sla_responded_at ?? w.created_at, w.closed_at))}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                      ({w.sla_responded_at ? 'In Progress' : 'Created'} → Closed)
                    </Typography>
                  </Stack>
                </SideField>
              )}

              {/* How long it sat open/in-progress before being closed —
                  created_at to the (possibly reopened-and-reclosed)
                  closed_at, same start point as the Reports "MTTR" KPI. */}
              {w.closed_at && (
                <SideField label="Open → Closed">
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <ScheduleOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      {formatMinutes(durationMinutes(w.created_at, w.closed_at))}
                    </Typography>
                  </Stack>
                </SideField>
              )}

              {/* Gap between the assignee closing it and the reporter
                  acknowledging/resolving it. `resolved` has no outgoing
                  transitions (see work-orders.service.js), so once set
                  closed_at reflects the same close that led here. */}
              {w.resolved_at && w.closed_at && (
                <SideField label="Closed → Resolved">
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <ScheduleOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      {formatMinutes(durationMinutes(w.closed_at, w.resolved_at))}
                    </Typography>
                  </Stack>
                </SideField>
              )}

              {w.asset_name && (
                <SideField label="Asset">
                  <Stack direction="row" alignItems="center" spacing={0.75}
                    onClick={() => w.asset_id && navigate(`/assets/${w.asset_id}`)}
                    sx={{ cursor: w.asset_id ? 'pointer' : 'default', '&:hover': w.asset_id ? { color: 'primary.main' } : {} }}>
                    <Inventory2OutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: w.asset_id ? 'primary.main' : 'text.primary' }}>{w.asset_name}</Typography>
                      <Typography sx={{ fontSize: 11.5, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>{w.asset_code}</Typography>
                    </Box>
                  </Stack>
                </SideField>
              )}

              {location && (
                <SideField label="Location">
                  <Stack direction="row" alignItems="center" spacing={0.75}
                    onClick={() => w.location_id ? navigate(`/locations/${w.location_id}`) : undefined}
                    sx={{ cursor: w.location_id ? 'pointer' : 'default', '&:hover': w.location_id ? { color: 'primary.main' } : {} }}>
                    <LocationOnOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: w.location_id ? 'primary.main' : 'text.primary' }}>{location}</Typography>
                  </Stack>
                </SideField>
              )}

              {w.category && (
                <SideField label="Categories">
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <CategoryOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {w.category.split(',').map((c, i) => (
                        <Chip key={i} label={c.trim()} size="small"
                          sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: 'action.hover', color: 'text.secondary' }} />
                      ))}
                    </Stack>
                  </Stack>
                </SideField>
              )}

              {/* Reporter is editable post-creation by anyone with
                  work_orders:update. The handover is recorded in the
                  audit trail (work_order.reporter_changed). Once the WO
                  is resolved the field is locked — at that point the
                  reporter has already done their final acknowledgment
                  and changing it would muddle the record. */}
              <SideField label="Reporter / Reviewer">
                <SearchableSelect
                  value={w.reporter_id ?? ''}
                  disabled={isPreview || w.status === 'resolved' || !can('work_orders:update')}
                  onChange={(uid) => {
                    const next = uid || null
                    apiNs.update(w.id, { reporter_id: next }).then(() => {
                      qc.invalidateQueries({ queryKey: ['work-order', w.id] })
                      qc.invalidateQueries({ queryKey: [listKey] })
                      toast.success('Reporter updated')
                    }).catch(() => toast.error('Failed'))
                  }}
                  options={users}
                  getValue={(u) => u.id}
                  getLabel={(u) => u.name}
                  placeholder="No reporter"
                  renderOption={(u) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: userColor(u.id) }} />
                      <Box>
                        <Typography sx={{ fontSize: 12.5 }}>{u.name}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{(u as any).role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                      </Box>
                    </Stack>
                  )}
                />
              </SideField>

              <SideField label="Created">
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{relativeTime(new Date(w.created_at), formatDateShort)}</Typography>
              </SideField>

              {/* Schedule details — surfaced for: (a) recurring templates
                  themselves (the "rule" row, shown in the Scheduled tab),
                  (b) child WOs that were spawned from a template, and
                  (c) one-shot future WOs that haven't activated yet.
                  Stays hidden for ordinary reactive WOs so the sidebar
                  doesn't grow an empty row. */}
              <ScheduleSideField wo={w} />
            </Stack>

            {/* Checklist */}
            {w.checklist_id && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>
                  Checklist
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}
                  onClick={() => navigate(`/checklists/${w.checklist_id}`)}
                  sx={{ cursor: 'pointer', p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                    transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' } }}>
                  <ChecklistOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', flex: 1 }}>View checklist</Typography>
                  <OpenInNewOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                </Stack>
              </>
            )}

            {/* Parts */}
            {woParts.length > 0 && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>
                  Parts used ({woParts.length})
                </Typography>
                <Stack spacing={1}>
                  {woParts.map((p: any) => (
                    <Stack key={p.id} direction="row" alignItems="center" justifyContent="space-between"
                      sx={{ px: 1.5, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{p.part_name}</Typography>
                        <Typography sx={{ fontSize: 10.5, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>{p.sku ?? p.part_code}</Typography>
                      </Box>
                      <Stack alignItems="flex-end">
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>x{p.quantity}</Typography>
                        {p.deducted && <Chip label="Deducted" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#05966918', color: '#059669' }} />}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </Box>
        </Stack>
      </Box>
    </Drawer>

    {/* More menu */}
    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
      PaperProps={{ sx: { borderRadius: 2, minWidth: 200 } }}>
      <MenuItem onClick={() => { setMenuAnchor(null); setQrOpen(true) }}
        sx={{ fontSize: 13 }}>
        <ListItemIcon><QrCode2OutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
        <ListItemText>Show QR code</ListItemText>
      </MenuItem>
      {/* Edit — same dialog as Duplicate/Create, pre-filled from this
          exact record, but submits via update() against this id
          instead of creating a new one. Closes the drawer first so
          the dialog isn't rendered underneath it. */}
      {!isPreview && onEdit && can('work_orders:update') && (
        <MenuItem onClick={() => {
          setMenuAnchor(null)
          const source = w
          onClose()
          onEdit(source)
        }} sx={{ fontSize: 13 }}>
          <ListItemIcon><EditOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText>Edit {labelLower}</ListItemText>
        </MenuItem>
      )}
      {/* Duplicate — opens the create dialog pre-filled with every
          editable field. Closes the drawer first so the create dialog
          isn't rendered underneath it. Nothing is created until the
          user submits the form. */}
      {!isPreview && onDuplicate && can('work_orders:create') && (
        <MenuItem onClick={() => {
          // `w` here is the freshly-fetched WorkOrder itself (see
          // woQ.data usage above), not the DummyWorkOrder shape — so
          // pass it straight through, no `._raw` unwrap needed.
          setMenuAnchor(null)
          const source = w
          onClose()
          onDuplicate(source)
        }} sx={{ fontSize: 13 }}>
          <ListItemIcon><ContentCopyOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText>Duplicate {labelLower}</ListItemText>
        </MenuItem>
      )}
      {!isPreview && can('work_orders:delete') && w.status !== 'resolved' && (
        <MenuItem onClick={() => { setMenuAnchor(null); setDeleteConfirmOpen(true) }}
          sx={{ color: 'error.main', fontSize: 13 }}>
          <ListItemIcon><DeleteOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Delete {labelLower}</ListItemText>
        </MenuItem>
      )}
      {!isPreview && can('work_orders:delete') && w.status === 'resolved' && (
        <MenuItem disabled sx={{ fontSize: 13 }}>
          <ListItemIcon><DeleteOutlineIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText>Resolved — cannot delete</ListItemText>
        </MenuItem>
      )}
    </Menu>

    <QrCodeDialog open={qrOpen} onClose={() => setQrOpen(false)}
      payload={qrPayload.workOrder(w.id)}
      title={w.title}
      subtitle={w.wo_code ?? undefined}
      kind={labelCap} />

    {/* Delete confirmation dialog */}
    <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>Delete {labelLower}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.6 }}>
            Are you sure you want to delete <strong>{w.title}</strong>? This {labelLower} will be removed from all views.
          </Typography>
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#FEF2F2', border: '1px solid #FECACA' }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <WarningAmberOutlinedIcon sx={{ fontSize: 20, color: '#DC2626', mt: 0.25 }} />
              <Typography sx={{ fontSize: 12.5, color: '#991B1B', lineHeight: 1.55 }}>
                This action cannot be undone from the UI. The {labelLower} and its associated data will be soft-deleted.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ color: 'text.secondary', fontWeight: 600 }}>Cancel</Button>
        <Button variant="contained" color="error" disabled={deleteM.isPending}
          onClick={() => { deleteM.mutate(); setDeleteConfirmOpen(false) }}
          sx={{ fontWeight: 600, borderRadius: 2 }}>
          {deleteM.isPending ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Acknowledge confirmation dialog */}
    <Dialog open={ackConfirmOpen} onClose={() => setAckConfirmOpen(false)} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>Acknowledge & Resolve</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.6 }}>
            You are about to resolve this work order. Please make sure you have thoroughly reviewed all the details before proceeding.
          </Typography>
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#FFF7ED', border: '1px solid #FDBA7440' }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <WarningAmberOutlinedIcon sx={{ fontSize: 20, color: '#D97706', mt: 0.25 }} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#92400E', mb: 0.5 }}>This action is irreversible</Typography>
                <Typography sx={{ fontSize: 12.5, color: '#B45309', lineHeight: 1.55 }}>
                  Please review the work order details, checklist results, and parts used at least 1 to 3 times before proceeding. Once resolved, this ticket cannot be reopened.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={() => setAckConfirmOpen(false)} sx={{ color: 'text.secondary', fontWeight: 600 }}>Cancel</Button>
        <Button variant="contained" color="warning" disabled={acknowledgeM.isPending}
          onClick={beginAcknowledge}
          sx={{ fontWeight: 600, borderRadius: 2 }}>
          {acknowledgeM.isPending ? 'Resolving...' : 'Proceed & Resolve'}
        </Button>
      </DialogActions>
    </Dialog>

    <DigitalSigner
      open={signerAction != null}
      title={signerAction === 'acknowledge' ? 'Sign to acknowledge' : 'Sign to close'}
      subtitle={signerAction === 'acknowledge'
        ? `Your signature will be recorded against ${woCode}.`
        : `Confirm the ${labelLower} is complete by signing ${woCode}.`}
      onClose={() => { if (!signerBusy) setSignerAction(null) }}
      onConfirm={handleSignatureConfirm}
      busy={signerBusy || closeM.isPending || acknowledgeM.isPending}
    />
    </>
  )
}

/// Renders the captured digital signatures for a WO. Only used
/// (consumed) signatures appear — an uploaded-but-never-consumed
/// row would indicate an aborted close / acknowledge attempt and
/// shouldn't be displayed as proof-of-sign-off. Hidden entirely
/// when there's nothing to show, so the section doesn't add visual
/// noise to WOs that never had a signature gate.
function SignaturesSection({ signatures }: { signatures: WorkOrderSignature[] }) {
  const { formatDate } = useOrgSettings()
  const ACTION_LABEL: Record<WorkOrderSignature['action'], string> = {
    close: 'Closed by',
    acknowledge: 'Resolved by',
  }
  const used = signatures.filter((s) => s.used_at)
  if (used.length === 0) return null
  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>
        Digital signatures
      </Typography>
      <Box sx={{
        display: 'grid', gap: 1.5,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
      }}>
        {used.map((s) => (
          <Box key={s.id} sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 2,
            p: 2, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
          }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {ACTION_LABEL[s.action] ?? s.action}
            </Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, mt: 0.5 }}>
              {s.signed_by_name ?? '—'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
              {formatDate(s.used_at ?? s.signed_at, true)}
            </Typography>
            {/* White plate behind the image so transparent areas in
                older PNGs still read as paper, not as the dark card. */}
            <Box sx={{
              bgcolor: '#fff', border: '1px solid', borderColor: 'divider',
              borderRadius: 1, p: 0.5, display: 'inline-block',
            }}>
              <Box
                component="img"
                src={s.signature_url}
                alt="signature"
                sx={{ display: 'block', maxWidth: '100%', maxHeight: 100 }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function WorkflowSection({ workOrder: w }: { workOrder: any }) {
  const { formatDateShort } = useOrgSettings()
  const workflowsQ = useQuery({ queryKey: ['workflows'], queryFn: () => api.workflows.list(), staleTime: 60_000 })
  const allWorkflows: Workflow[] = Array.isArray(workflowsQ.data) ? workflowsQ.data : []

  // Find workflows that match this WO's properties
  const matching = allWorkflows.filter((wf) => {
    if (!wf.is_active) return false
    const tt = wf.trigger_type
    if (!['work_order.created', 'work_order.closed', 'work_order.status_changed'].includes(tt)) return false
    const cfg = wf.trigger_config ?? {}
    if (cfg.priority && cfg.priority !== w.priority) return false
    if (cfg.work_type && cfg.work_type !== w.work_type) return false
    if (cfg.status && cfg.status !== w.status) return false
    return true
  })

  // Find runs that fired for this WO
  const runsQ = useQuery({
    queryKey: ['wo-workflow-runs', w.id],
    queryFn: async () => {
      const results: any[] = []
      for (const wf of matching) {
        try {
          const runs = await api.workflows.listRuns(wf.id)
          const woRuns = (Array.isArray(runs) ? runs : []).filter((r: any) => r.trigger_ref === w.id)
          for (const r of woRuns) results.push({ ...r, workflow_name: wf.name })
        } catch {}
      }
      return results
    },
    enabled: matching.length > 0,
    staleTime: 30_000,
  })
  const firedRuns: any[] = Array.isArray(runsQ.data) ? runsQ.data : []

  const STATUS_COLORS: Record<string, string> = { completed: '#059669', failed: '#DC2626', running: '#D97706' }

  if (matching.length === 0 && firedRuns.length === 0) return null

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>
        Automations
      </Typography>

      {/* Matching workflows */}
      {matching.length > 0 && (
        <Stack spacing={1} sx={{ mb: firedRuns.length > 0 ? 2 : 0 }}>
          {matching.map((wf) => {
            const fired = firedRuns.filter((r) => r.workflow_id === wf.id)
            const lastRun = fired[0]
            return (
              <Box key={wf.id} sx={{ px: 2, py: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                bgcolor: (t: any) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 99, bgcolor: '#7C3AED14', color: '#7C3AED',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <BoltOutlinedIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap>{wf.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {wf.trigger_type.replace(/\./g, ' → ')} · {wf.step_count ?? 0} steps
                    </Typography>
                  </Box>
                  {lastRun ? (
                    <Chip label={lastRun.status} size="small"
                      sx={{ height: 18, fontSize: 9, fontWeight: 700, textTransform: 'capitalize',
                        bgcolor: `${STATUS_COLORS[lastRun.status] ?? '#64748B'}14`,
                        color: STATUS_COLORS[lastRun.status] ?? '#64748B' }} />
                  ) : (
                    <Chip label="Will fire" size="small"
                      sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#D9770614', color: '#D97706' }} />
                  )}
                </Stack>
              </Box>
            )
          })}
        </Stack>
      )}

      {/* Fired runs history */}
      {firedRuns.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.75 }}>
            Run history
          </Typography>
          <Stack spacing={0.5}>
            {firedRuns.slice(0, 5).map((r) => (
              <Stack key={r.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: STATUS_COLORS[r.status] ?? '#64748B', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 11.5, flex: 1, minWidth: 0 }} noWrap>{r.workflow_name}</Typography>
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                  {formatDateShort(r.started_at)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}

function SideField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', mb: 0.75 }}>{label}</Typography>
      {children}
    </Box>
  )
}

/// Sidebar row that surfaces the WO's schedule story in three flavours:
///   - This WO IS a recurring template → show the rule + when it next
///     fires + when it last spawned a child.
///   - This WO was spawned from a template → link back to the template
///     and show the rule that produced it.
///   - One-shot future WO → show the activation moment.
/// For ordinary reactive WOs the component renders nothing so the
/// sidebar stays compact.
function ScheduleSideField({ wo }: { wo: any }) {
  const { formatDate, formatDateShort } = useOrgSettings()
  const raw = wo._raw ?? wo
  const isTemplate = !!raw.is_schedule_template
  const fromTemplateId: string | null = raw.schedule_template_id ?? null
  const scheduledFor: string | null = raw.scheduled_for ?? null
  const cfg = raw.schedule_config ?? null
  const lastSpawned: string | null = raw.last_spawned_at ?? null

  // Bail out early for ordinary WOs — keeps the sidebar uncluttered.
  if (!isTemplate && !fromTemplateId && !scheduledFor) return null

  // Recurring template — the row IS the rule. Show the pattern + the
  // next time it'll fire (computed client-side; the server scheduler
  // remains the source of truth).
  if (isTemplate && cfg) {
    const summary = summarizeSchedule(cfg) ?? 'Recurring'
    const next = nextFireMoment(cfg)
    return (
      <SideField label="Schedule">
        <Stack direction="row" alignItems="flex-start" spacing={0.75}>
          <EventRepeatOutlinedIcon sx={{ fontSize: 15, color: 'primary.main', mt: '2px' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
              Recurring template
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
              {summary}
            </Typography>
            {next && (
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>
                Next: {formatNextFire(next)}
              </Typography>
            )}
            {lastSpawned && (
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                Last spawn: {relativeTime(new Date(lastSpawned), formatDateShort)}
              </Typography>
            )}
          </Box>
        </Stack>
      </SideField>
    )
  }

  // Spawned child of a recurring template. The WO row itself doesn't
  // carry the parent's config (the server only stores the FK), so we
  // just say where it came from. Clicking it would be nice but we
  // don't have the parent in scope here.
  if (fromTemplateId) {
    return (
      <SideField label="Schedule">
        <Stack direction="row" alignItems="flex-start" spacing={0.75}>
          <EventRepeatOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', mt: '2px' }} />
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
              Spawned from a recurring schedule
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
              Generated automatically on a calendar trigger.
            </Typography>
          </Box>
        </Stack>
      </SideField>
    )
  }

  // One-shot — `scheduled_for` is a future activation moment. The
  // backend keeps the WO in 'scheduled' status until that instant.
  const when = new Date(scheduledFor!)
  return (
    <SideField label="Schedule">
      <Stack direction="row" alignItems="flex-start" spacing={0.75}>
        <ScheduleOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', mt: '2px' }} />
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
            Scheduled for {formatDate(when, true)}
          </Typography>
          {when.getTime() > Date.now() && (
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
              Activates in {formatNextFire(when)}
            </Typography>
          )}
        </Box>
      </Stack>
    </SideField>
  )
}

function formatMinutes(min: number | null | undefined): string {
  if (!min || min <= 0) return '0m'
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}

function durationMinutes(start: string | null | undefined, end: string | null | undefined): number {
  if (!start || !end) return 0
  return Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60_000))
}

const AVATAR_COLORS = ['#0D9488', '#D97706', '#2563EB', '#DC2626', '#7C3AED', '#EA580C', '#059669', '#DB2777']
function userColor(id: string) {
  let hash = 0
  for (const c of id) hash = ((hash << 5) - hash + c.charCodeAt(0)) | 0
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!
}

function relativeTime(d: Date, fallback?: (date: Date | string) => string): string {
  const diff = Date.now() - d.getTime()
  if (diff < 60_000) return 'just now'
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  // Once we pass the "Nd ago" cap we render the absolute date — use
  // the caller-supplied org-format helper so the format respects the
  // user's preference, falling back to ISO short on cold paths.
  return fallback ? fallback(d) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function CommentAttachment({ comment }: { comment: WorkOrderComment }) {
  const isImage = comment.file_type?.startsWith('image/')
  const isAudio = comment.file_type?.startsWith('audio/')
  // The MinIO bucket is set to public-read at startup, so we can point
  // <img>/<audio> at file_url directly. The browser handles caching and
  // streaming far better than a blob round-trip would.
  const src = comment.file_url ?? ''

  if (isImage) {
    return (
      <Box component="img" src={src} loading="lazy"
        onClick={() => window.open(src, '_blank')}
        sx={{ display: 'block', maxWidth: 280, maxHeight: 240, borderRadius: 1.5, cursor: 'zoom-in', objectFit: 'cover' }} />
    )
  }
  if (isAudio) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 220, maxWidth: 320 }}>
        <MicNoneOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Box component="audio" controls src={src} preload="metadata"
          sx={{ height: 32, flex: 1, minWidth: 0 }} />
      </Stack>
    )
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}
      onClick={() => window.open(src, '_blank')}
      sx={{
        px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer', maxWidth: 320,
        bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
        '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)' },
      }}>
      <AttachFileOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap>{comment.file_name ?? 'File'}</Typography>
        {comment.file_size && <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{formatBytes(comment.file_size)}</Typography>}
      </Box>
    </Stack>
  )
}

function SlaIndicator({ workOrder: w }: { workOrder: any }) {
  // Tick every 30s for live countdown (only for open WOs)
  const isActive = w.status === 'open' || w.status === 'in_progress'
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!isActive) return
    const id = setInterval(() => setTick((t) => t + 1), 30_000)
    return () => clearInterval(id)
  }, [isActive])

  const now = new Date()
  const responseDue = w.sla_response_due ? new Date(w.sla_response_due) : null
  const resolutionDue = w.sla_resolution_due ? new Date(w.sla_resolution_due) : null
  const respondedAt = w.sla_responded_at ? new Date(w.sla_responded_at) : null

  function timeLeft(due: Date | null) {
    if (!due) return null
    const diff = due.getTime() - now.getTime()
    if (diff <= 0) {
      const over = Math.abs(diff)
      const mins = Math.floor(over / 60000)
      if (mins < 60) return `Breached ${mins}m ago`
      const hrs = Math.floor(mins / 60)
      if (hrs < 24) return `Breached ${hrs}h ${mins % 60}m ago`
      return `Breached ${Math.floor(hrs / 24)}d ${hrs % 24}h ago`
    }
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins}m left`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ${mins % 60}m left`
    return `${Math.floor(hrs / 24)}d ${hrs % 24}h left`
  }

  function barPct(created: string, due: Date | null) {
    if (!due) return 0
    const total = due.getTime() - new Date(created).getTime()
    const elapsed = now.getTime() - new Date(created).getTime()
    return Math.min(100, Math.max(0, (elapsed / total) * 100))
  }

  const isClosed = w.status === 'closed' || w.status === 'resolved'
  const responseBreach = w.sla_breached && w.sla_breach_type === 'response'
  const resolutionBreach = w.sla_breached && (w.sla_breach_type === 'resolution' || w.sla_breach_type === 'response')

  // Response label
  let responseLabel: string
  if (respondedAt && responseDue) {
    const diff = respondedAt.getTime() - responseDue.getTime()
    responseLabel = diff > 0 ? `Responded late` : 'Responded on time'
  } else if (respondedAt) {
    responseLabel = 'Responded'
  } else {
    responseLabel = timeLeft(responseDue) ?? '—'
  }

  // Resolution label
  let resolutionLabel: string
  if (isClosed && w.closed_at && resolutionDue) {
    const closedTime = new Date(w.closed_at).getTime()
    const diff = closedTime - resolutionDue.getTime()
    resolutionLabel = diff > 0 ? `Resolved late` : 'Resolved on time'
  } else if (isClosed) {
    resolutionLabel = w.sla_breached ? 'Resolved late' : 'Resolved on time'
  } else {
    resolutionLabel = timeLeft(resolutionDue) ?? '—'
  }

  const responsePct = respondedAt ? 100 : barPct(w.created_at, responseDue)
  const resolutionPct = isClosed ? 100 : barPct(w.created_at, resolutionDue)

  const responseColor = responseBreach || responseLabel === 'Responded late' ? '#DC2626' : respondedAt ? '#059669' : responsePct > 80 ? '#D97706' : '#3B82F6'
  const resolutionColor = resolutionBreach || resolutionLabel === 'Resolved late' ? '#DC2626' : isClosed ? '#059669' : resolutionPct > 80 ? '#D97706' : '#3B82F6'

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          SLA
        </Typography>
        {w.sla_policy_name && (
          <Chip label={w.sla_policy_name} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary' }} />
        )}
        {w.sla_breached && (
          <Chip label="BREACHED" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 800, bgcolor: '#FEE2E2', color: '#DC2626' }} />
        )}
      </Stack>
      <Stack spacing={1.5}>
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Response</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: responseColor }}>{responseLabel}</Typography>
          </Stack>
          <Box sx={{ height: 6, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', borderRadius: 99, width: `${responsePct}%`, bgcolor: responseColor, transition: 'width 0.5s ease' }} />
          </Box>
        </Box>
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Resolution</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: resolutionColor }}>{resolutionLabel}</Typography>
          </Stack>
          <Box sx={{ height: 6, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', borderRadius: 99, width: `${resolutionPct}%`, bgcolor: resolutionColor, transition: 'width 0.5s ease' }} />
          </Box>
        </Box>
      </Stack>
    </Box>
  )
}

const RECURRENCE_LABELS: Record<string, string> = {
  daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly',
}

function RecurrenceSection({ workOrder: w }: { workOrder: any }) {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const { formatDate } = useOrgSettings()
  // Match parent drawer's kind-routing so the chain + stop-recurrence
  // calls go through `tasks:*` perms when the WO is actually a task.
  const apiNs = (w?.kind === 'task') ? api.tasks : api.workOrders
  const listKey = (w?.kind === 'task') ? 'tasks' : 'work-orders'

  const chainQ = useQuery({
    queryKey: ['recurrence-chain', w.id],
    queryFn: () => apiNs.getRecurrenceChain(w.id),
  })
  const chain: any[] = Array.isArray(chainQ.data) ? chainQ.data : []
  const currentIdx = chain.findIndex((c) => c.id === w.id)
  const rootWo = chain[0]

  const stopM = useMutation({
    mutationFn: () => apiNs.stopRecurrence(w.id),
    onSuccess: () => {
      toast.success('Recurrence stopped')
      qc.invalidateQueries({ queryKey: ['work-order', w.id] })
      qc.invalidateQueries({ queryKey: [listKey] })
      qc.invalidateQueries({ queryKey: ['recurrence-chain', w.id] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const STATUS_DOT: Record<string, string> = { open: '#F59E0B', in_progress: '#3B82F6', closed: '#059669', resolved: '#059669' }

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
            Recurrence
          </Typography>
          <Chip label={RECURRENCE_LABELS[w.recurrence] ?? w.recurrence} size="small"
            sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#7C3AED14', color: '#7C3AED' }} />
          {w.occurrence_number > 1 && (
            <Chip label={`#${w.occurrence_number}`} size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary' }} />
          )}
        </Stack>
        {w.recurrence_active && (
          <Button size="small" color="error" onClick={() => confirm({
            title: 'Stop recurrence?',
            message: `No more ${w?.kind === 'task' ? 'tasks' : 'work orders'} will be auto-created from this recurring schedule. This cannot be undone.`,
            confirmLabel: 'Stop recurrence', variant: 'danger',
          }).then((ok) => ok && stopM.mutate())}
            sx={{ fontSize: 11, fontWeight: 600 }}>
            Stop
          </Button>
        )}
        {!w.recurrence_active && w.recurrence !== 'none' && (
          <Chip label="Stopped" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#DC262614', color: '#DC2626' }} />
        )}
      </Stack>

      {/* Chain info */}
      {rootWo && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
          Started by <strong>{rootWo.created_by_name}</strong> on {formatDate(rootWo.created_at)}
          {chain.length > 1 && ` · ${chain.length} occurrences`}
        </Typography>
      )}

      {/* Chain timeline */}
      {chain.length > 1 && (
        <Stack spacing={0.5}>
          {chain.slice(-5).map((occ, i) => {
            const isCurrent = occ.id === w.id
            return (
              <Stack key={occ.id} direction="row" alignItems="center" spacing={1}
                sx={{ px: 1.5, py: 0.75, borderRadius: 1, bgcolor: isCurrent ? (t: any) => `${t.palette.primary.main}06` : 'transparent',
                  border: isCurrent ? '1px solid' : '1px solid transparent', borderColor: isCurrent ? 'primary.main' : 'transparent' }}>
                <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: STATUS_DOT[occ.status] ?? '#94A3B8', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12, fontWeight: isCurrent ? 700 : 500, flex: 1 }} noWrap>
                  #{occ.occurrence_number} — {occ.status.replace('_', ' ')}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                  {formatDate(occ.created_at)}
                </Typography>
              </Stack>
            )
          })}
          {chain.length > 5 && (
            <Typography sx={{ fontSize: 11, color: 'text.disabled', textAlign: 'center' }}>
              + {chain.length - 5} earlier occurrences
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  )
}

const AUDIT_ICONS: Record<string, { label: string; color: string }> = {
  'work_order.created':       { label: 'Created', color: '#3B82F6' },
  'work_order.updated':       { label: 'Updated', color: '#8B5CF6' },
  'work_order.assigned':      { label: 'Assigned', color: '#7C3AED' },
  'work_order.executed':      { label: 'Work started', color: '#D97706' },
  'work_order.closed':        { label: 'Closed', color: '#059669' },
  'work_order.acknowledged':  { label: 'Resolved', color: '#059669' },
  'work_order.rejected':      { label: 'Rejected', color: '#DC2626' },
  'work_order.reopened':      { label: 'Reopened', color: '#EA580C' },
  'work_order.comment_added': { label: 'Comment', color: '#64748B' },
  'work_order.recurrence_stopped': { label: 'Recurrence stopped', color: '#DC2626' },
  'work_order.status_changed': { label: 'Status changed', color: '#8B5CF6' },
}

function WoAuditTrail({ workOrderId }: { workOrderId: string }) {
  const { formatDate } = useOrgSettings()

  const auditQ = useQuery({
    queryKey: ['wo-audit', workOrderId],
    queryFn: async () => {
      // Scoped server-side to this WO's own resource_id — previously
      // fetched the org-wide top-50 most recent work_order audit rows
      // and filtered client-side, which silently dropped this WO's
      // real events once enough other activity happened elsewhere in
      // the org afterward and pushed them outside that global window.
      const res = await api.analytics.auditLogs({ module: 'work_order', resource_id: workOrderId, limit: 50 })
      return Array.isArray(res?.logs) ? res.logs : []
    },
    staleTime: 30_000,
  })
  const entries: any[] = Array.isArray(auditQ.data) ? auditQ.data : []

  if (entries.length === 0) return null

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 2 }}>
        Timeline · {entries.length}
      </Typography>
      <Stack spacing={0}>
        {entries.slice(0, 15).map((e, i) => {
          const meta = AUDIT_ICONS[e.action_full ?? e.action] ?? AUDIT_ICONS[`work_order.${e.action}`] ?? { label: (e.action ?? '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()), color: '#64748B' }
          const isLast = i === Math.min(entries.length, 15) - 1
          const role = e.actor_role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())

          return (
            <Stack key={e.id ?? i} direction="row" spacing={1.5} sx={{ position: 'relative' }}>
              {/* Timeline line + avatar */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
                <UserAvatar name={e.actor_name} avatarUrl={e.actor_avatar} size={28}
                  sx={{ bgcolor: meta.color, border: '2px solid', borderColor: 'background.paper', zIndex: 1 }} />
                {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mt: -0.25 }} />}
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, pb: isLast ? 0 : 2, pt: 0.25 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{e.actor_name ?? 'System'}</Typography>
                  {role && (
                    <Chip label={role} size="small"
                      sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: `${meta.color}14`, color: meta.color }} />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: meta.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>{meta.label}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{formatDate(e.created_at)}</Typography>
                </Stack>
                {e.metadata && (e.metadata.notes || e.metadata.assigned_to_user_id) && (
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5, fontStyle: 'italic' }}>
                    {e.metadata.notes ?? ''}
                  </Typography>
                )}
              </Box>
            </Stack>
          )
        })}
      </Stack>
    </Box>
  )
}
